import React, { useState, useRef } from "react";
import { Select, Spin } from "antd";

type OptionType = {
  label: string;
  value: string | number;
};

type LazySelectProps<T> = {
  value?: any;
  onChange?: (value: any) => void;
  fetchOptions: (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => Promise<T[]>;
  mapOption: (item: T) => OptionType;
  pageSize?: number;
};

export function LazySelect<T>({
  value,
  onChange,
  fetchOptions,
  mapOption,
  pageSize = 20,
}: LazySelectProps<T>) {
  const [options, setOptions] = useState<OptionType[]>([]);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const searchRef = useRef<string>("");

  const loadOptions = async (search = "", nextPage = 1) => {
    setFetching(true);

    const data = await fetchOptions({
      search,
      page: nextPage,
      pageSize,
    });

    const mapped = data.map(mapOption);

    setOptions((prev) => {
      const map = new Map(prev.map((o) => [o.value, o]));

      mapped.forEach((item) => {
        map.set(item.value, item);
      });

      return Array.from(map.values());
    });

    setHasMore(data.length === pageSize);
    setFetching(false);
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      options={options}
      placeholder="Select"
      notFoundContent={fetching ? <Spin size="small" /> : null}
      showSearch={{
        filterOption: false,
        onSearch: (val) => {
          searchRef.current = val;
          setPage(1);
          setOptions([]);
          loadOptions(val, 1);
        },
      }}
      onFocus={() => {
        if (!options.length) {
          loadOptions("", 1);
        }
      }}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement;

        if (
          target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
          hasMore &&
          !fetching
        ) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadOptions(searchRef.current, nextPage);
        }
      }}
    />
  );
}
